import React, { useMemo, useState } from 'react';
import ModalOverlay from '../../shared/ModalOverlay';
import { Report, ReportRunTarget, Record, Vendor } from '../../types';

interface ReportsListModalProps {
  active: boolean;
  onClose: () => void;
  reports: Report[];
  records: Record[];
  vendors: Vendor[];
  onRun: (targets: ReportRunTarget[]) => Promise<void> | void;
  onDownload: (id: string) => void;
  onSend: (id: string) => void;
  onDelete: (id: string) => void;
}

const COMPONENT_MAINTENANCE = 'Component Maintenance';

const TYPE_LABEL: { [key: string]: string } = {
  carrier: 'Carrier',
  component_maintenance: 'Component Maintenance',
};

interface RunOption extends ReportRunTarget {
  key: string;
}

const ReportsListModal: React.FC<ReportsListModalProps> = ({
  active, onClose, reports, records, vendors, onRun, onDownload, onSend, onDelete,
}) => {
  const [typeFilter, setTypeFilter] = useState('');
  const [periodFilter, setPeriodFilter] = useState('');
  const [running, setRunning] = useState(false);
  const [runPickerOpen, setRunPickerOpen] = useState(false);
  const [selectedKeys, setSelectedKeys] = useState<Set<string>>(new Set());

  // Every report that could be generated: one per carrier, one per Component
  // Maintenance vendor — derived from the loaded records and vendors.
  const runOptions = useMemo<RunOption[]>(() => {
    const carriers = Array.from(new Set(records.map((r) => (r.carrier || '').trim()).filter(Boolean))).sort();
    const cmVendors = Array.from(new Set(
      vendors.filter((v) => v.vendor_type === COMPONENT_MAINTENANCE).map((v) => (v.vendor_name || '').trim()).filter(Boolean),
    )).sort();
    const carrierOptions: RunOption[] = carriers.map((c) => ({ report_type: 'carrier', entity_name: c, key: `carrier::${c}` }));
    const vendorOptions: RunOption[] = cmVendors.map((v) => ({ report_type: 'component_maintenance', entity_name: v, key: `component_maintenance::${v}` }));
    return [...carrierOptions, ...vendorOptions];
  }, [records, vendors]);

  const periods = Array.from(new Set(reports.map((r) => r.period))).sort().reverse();

  let filtered = reports;
  if (typeFilter) filtered = filtered.filter((r) => r.report_type === typeFilter);
  if (periodFilter) filtered = filtered.filter((r) => r.period === periodFilter);

  const openRunPicker = (): void => {
    setSelectedKeys(new Set(runOptions.map((o) => o.key)));
    setRunPickerOpen(true);
  };

  const toggleKey = (key: string): void => {
    setSelectedKeys((prev) => {
      const next = new Set(prev);
      if (next.has(key)) next.delete(key);
      else next.add(key);
      return next;
    });
  };

  const allSelected = runOptions.length > 0 && selectedKeys.size === runOptions.length;
  const toggleAll = (): void => {
    setSelectedKeys(allSelected ? new Set() : new Set(runOptions.map((o) => o.key)));
  };

  const confirmRun = async (): Promise<void> => {
    const targets: ReportRunTarget[] = runOptions
      .filter((o) => selectedKeys.has(o.key))
      .map(({ report_type, entity_name }) => ({ report_type, entity_name }));
    setRunning(true);
    try {
      await onRun(targets);
      setRunPickerOpen(false);
    } finally {
      setRunning(false);
    }
  };

  const carrierOptions = runOptions.filter((o) => o.report_type === 'carrier');
  const vendorOptions = runOptions.filter((o) => o.report_type === 'component_maintenance');

  const renderOptionGroup = (title: string, options: RunOption[]): React.ReactNode => (
    options.length > 0 && (
      <div style={{ marginBottom: '0.75rem' }}>
        <div style={{ fontSize: '0.75rem', textTransform: 'uppercase', color: 'var(--text-dim)', marginBottom: '0.25rem' }}>{title}</div>
        {options.map((o) => (
          <label key={o.key} className="filter-status-check" style={{ display: 'flex', gap: '0.4rem', padding: '0.15rem 0' }}>
            <input type="checkbox" checked={selectedKeys.has(o.key)} onChange={() => toggleKey(o.key)} />
            {o.entity_name}
          </label>
        ))}
      </div>
    )
  );

  return (
    <ModalOverlay active={active} onClose={onClose}>
      <div className="modal modal-narrow">
        <div className="modal-header">
          <h2>Reports</h2>
          <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
            <button className="btn btn-primary btn-sm" onClick={openRunPicker} title="Generate reports now">
              Run Reports
            </button>
            <button className="modal-close" onClick={onClose}>&times;</button>
          </div>
        </div>
        <div style={{ padding: '0.75rem 1.5rem 0' }}>
          <div className="poc-list-filters">
            <div className="form-group" style={{ flex: 1 }}>
              <label>Type</label>
              <select value={typeFilter} onChange={(e) => setTypeFilter(e.target.value)}>
                <option value="">All Types</option>
                <option value="carrier">Carrier</option>
                <option value="component_maintenance">Component Maintenance</option>
              </select>
            </div>
            <div className="form-group" style={{ flex: 1 }}>
              <label>Period</label>
              <select value={periodFilter} onChange={(e) => setPeriodFilter(e.target.value)}>
                <option value="">All Periods</option>
                {periods.map((p) => <option key={p} value={p}>{p}</option>)}
              </select>
            </div>
          </div>
        </div>
        <div style={{ padding: '0.5rem 1.5rem 1rem', maxHeight: '400px', overflowY: 'auto' }}>
          {!filtered.length ? (
            <p style={{ color: 'var(--text-dim)', textAlign: 'center', padding: '1rem' }}>
              No reports yet. Use “Run Reports” to generate them, or wait for the last day of the month.
            </p>
          ) : (
            filtered.map((r) => (
              <div key={r.id} className="poc-list-item">
                <div className="poc-list-item-info">
                  <span className="poc-list-item-name">{r.entity_name}</span>
                  <span className="poc-list-item-vendor">
                    {TYPE_LABEL[r.report_type] || r.report_type} · {r.period_label} · {r.record_count} record(s)
                  </span>
                </div>
                <div style={{ display: 'flex', gap: '0.3rem' }}>
                  <button className="btn-icon" title="Download PDF" onClick={() => onDownload(r.id)}>
                    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                      <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4" />
                      <polyline points="7 10 12 15 17 10" />
                      <line x1="12" y1="15" x2="12" y2="3" />
                    </svg>
                  </button>
                  <button className="btn-icon" title="Send report" onClick={() => onSend(r.id)}>
                    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                      <line x1="22" y1="2" x2="11" y2="13" />
                      <polygon points="22 2 15 22 11 13 2 9 22 2" />
                    </svg>
                  </button>
                  <button className="btn-icon delete" title="Delete report" onClick={() => onDelete(r.id)}>
                    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                      <polyline points="3 6 5 6 21 6" />
                      <path d="M19 6v14a2 2 0 0 1-2 2H7a2 2 0 0 1-2-2V6m3 0V4a2 2 0 0 1 2-2h4a2 2 0 0 1 2 2v2" />
                    </svg>
                  </button>
                </div>
              </div>
            ))
          )}
        </div>

        {runPickerOpen && (
          <div className="modal-overlay active" style={{ zIndex: 120 }} onClick={(e) => { if (e.target === e.currentTarget && !running) setRunPickerOpen(false); }}>
            <div className="modal modal-narrow">
              <div className="modal-header">
                <h2>Run Reports</h2>
                <button className="modal-close" onClick={() => { if (!running) setRunPickerOpen(false); }}>&times;</button>
              </div>
              <div style={{ padding: '0.75rem 1.5rem 0' }}>
                <label className="filter-status-check" style={{ display: 'flex', gap: '0.4rem', fontWeight: 600 }}>
                  <input type="checkbox" checked={allSelected} onChange={toggleAll} />
                  Run every report ({runOptions.length})
                </label>
              </div>
              <div style={{ padding: '0.5rem 1.5rem 0.5rem', maxHeight: '320px', overflowY: 'auto' }}>
                {runOptions.length === 0 ? (
                  <p style={{ color: 'var(--text-dim)', textAlign: 'center' }}>No report targets available.</p>
                ) : (
                  <>
                    {renderOptionGroup('Carriers', carrierOptions)}
                    {renderOptionGroup('Component Maintenance Vendors', vendorOptions)}
                  </>
                )}
              </div>
              <div className="form-actions">
                <button type="button" className="btn btn-secondary" onClick={() => setRunPickerOpen(false)} disabled={running}>Cancel</button>
                <button type="button" className="btn btn-primary" onClick={confirmRun} disabled={running || selectedKeys.size === 0}>
                  {running ? 'Running…' : `Run ${selectedKeys.size} report(s)`}
                </button>
              </div>
            </div>
          </div>
        )}
      </div>
    </ModalOverlay>
  );
};

export default ReportsListModal;
